import { useState, useEffect } from 'react';
import Navigation from '../components/landing/Navigation';
import Footer from '../components/landing/Footer';

const sections = [
  { id: 'acceptance', title: 'Acceptance of Terms' },
  { id: 'description', title: 'Service Description' },
  { id: 'accounts', title: 'User Accounts' },
  { id: 'payment', title: 'Payment and Billing' },
  { id: 'use-license', title: 'Use License' },
  { id: 'prohibited', title: 'Prohibited Uses' },
  { id: 'data-ownership', title: 'Data Ownership' },
  { id: 'intellectual-property', title: 'Intellectual Property' },
  { id: 'termination', title: 'Termination' },
  { id: 'warranties', title: 'Disclaimers and Warranties' },
  { id: 'liability', title: 'Limitation of Liability' },
  { id: 'indemnification', title: 'Indemnification' },
  { id: 'changes', title: 'Changes to Terms' },
  { id: 'contact', title: 'Contact Information' }
];

export default function TermsOfService() {
  const [activeSection, setActiveSection] = useState('acceptance');

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(s => document.getElementById(s.id));
      const scrollPosition = window.scrollY + 200;

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const section = sectionElements[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="pt-24 pb-20 px-8">
        <div className="w-full max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12">
            
            {/* Table of Contents - Sticky Sidebar */}
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-24">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Contents</h3>
                <nav className="space-y-2">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-100 text-blue-700 font-semibold'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {section.title}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="lg:col-span-9">
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm">
                
                <h1 
                  className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Terms of Service
                </h1>
                <p className="text-gray-600 mb-8">Last updated: December 17, 2025</p>

                {/* Acceptance */}
                <section id="acceptance" className="mb-12 scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Acceptance of Terms</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Welcome to Inked.fit. These Terms of Service ("Terms") govern your access to and use of our attendance management service. By accessing or using our service, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the service.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    These Terms constitute a legally binding agreement between you and Inked.fit. We reserve the right to update these Terms at any time, and your continued use of the service after such changes constitutes your acceptance of the new Terms.
                  </p>
                </section>

                {/* Description */}
                <section id="description" className="mb-12 scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Service Description</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Inked.fit provides a cloud-based attendance management system that enables organizations to track attendance through QR code scanning and real-time reporting. Our service includes:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>QR code-based check-in system</li>
                    <li>Attendance tracking and reporting</li>
                    <li>Organization and member management</li>
                    <li>Offline attendance sync capabilities</li>
                    <li>Data export functionality</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    We reserve the right to modify, suspend, or discontinue any aspect of the service at any time without prior notice.
                  </p>
                </section>

                {/* Accounts */}
                <section id="accounts" className="mb-12 scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">User Accounts</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    To use our service, you must create an account and provide accurate, complete information. You are responsible for:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                    <li>Maintaining the confidentiality of your account credentials</li>
                    <li>All activities that occur under your account</li>
                    <li>Notifying us immediately of any unauthorized access</li>
                    <li>Ensuring all information provided is accurate and up-to-date</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed">
                    You may not share your account credentials or allow others to access your account. We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.
                  </p>
                </section>

                {/* Payment */}
                <section id="payment" className="mb-12 scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment and Billing</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Our service is provided on a subscription basis at $299 per month per organization. Payment terms:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                    <li><strong>Billing Cycle:</strong> Monthly subscription, billed in advance</li>
                    <li><strong>Payment Methods:</strong> We accept major credit cards and other payment methods as specified</li>
                    <li><strong>Auto-Renewal:</strong> Subscriptions automatically renew unless cancelled before the renewal date</li>
                    <li><strong>Refunds:</strong> Refunds are provided on a case-by-case basis at our discretion</li>
                    <li><strong>Failed Payments:</strong> Services may be suspended if payment fails</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed">
                    You authorize us to charge your payment method for all fees incurred. Prices are subject to change with 30 days' notice.
                  </p>
                </section>

                {/* Use License */}
                <section id="use-license" className="mb-12 scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Use License</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use our service for your internal business purposes. This license does not include the right to:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>Modify, copy, or create derivative works of the service</li>
                    <li>Reverse engineer, decompile, or disassemble the service</li>
                    <li>Remove any copyright or proprietary notices</li>
                    <li>Transfer, sublicense, or resell the service to third parties</li>
                    <li>Use the service for competitive analysis or benchmarking</li>
                  </ul>
                </section>

                {/* Prohibited Uses */}
                <section id="prohibited" className="mb-12 scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Prohibited Uses</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    You agree not to use the service for any unlawful purpose or in any way that:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>Violates any applicable laws or regulations</li>
                    <li>Infringes on the rights of others</li>
                    <li>Contains malware, viruses, or harmful code</li>
                    <li>Attempts to gain unauthorized access to our systems</li>
                    <li>Interferes with the proper functioning of the service</li>
                    <li>Harasses, abuses, or harms other users</li>
                    <li>Impersonates another person or entity</li>
                  </ul>
                </section>

                {/* Data Ownership */}
                <section id="data-ownership" className="mb-12 scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Ownership</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    You retain all rights to the data you submit to our service ("Your Data"). By using our service, you grant us a limited license to:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                    <li>Store and process Your Data to provide the service</li>
                    <li>Use aggregated, anonymized data for service improvement and analytics</li>
                    <li>Back up Your Data for disaster recovery purposes</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed">
                    You are responsible for maintaining backups of Your Data. Upon termination, we will delete Your Data within 90 days unless legally required to retain it.
                  </p>
                </section>

                {/* Intellectual Property */}
                <section id="intellectual-property" className="mb-12 scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Intellectual Property</h2>
                  <p className="text-gray-700 leading-relaxed">
                    The service, including all content, features, functionality, software, and design, is owned by Inked.fit and is protected by international copyright, trademark, and other intellectual property laws. Our trademarks and trade dress may not be used without our prior written permission.
                  </p>
                </section>

                {/* Termination */}
                <section id="termination" className="mb-12 scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Termination</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Either party may terminate the service agreement:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                    <li><strong>By You:</strong> Cancel your subscription at any time through your account settings</li>
                    <li><strong>By Us:</strong> We may suspend or terminate your access immediately if you violate these Terms</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed">
                    Upon termination, your right to use the service ceases immediately. We will retain Your Data for 90 days to allow for recovery, after which it will be permanently deleted.
                  </p>
                </section>

                {/* Warranties */}
                <section id="warranties" className="mb-12 scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Disclaimers and Warranties</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>Warranties of merchantability and fitness for a particular purpose</li>
                    <li>Warranties of non-infringement</li>
                    <li>Warranties that the service will be uninterrupted, secure, or error-free</li>
                    <li>Warranties regarding the accuracy or reliability of any information</li>
                  </ul>
                </section>

                {/* Liability */}
                <section id="liability" className="mb-12 scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, INKED.FIT SHALL NOT BE LIABLE FOR:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                    <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                    <li>Loss of profits, revenue, data, or use</li>
                    <li>Damages resulting from unauthorized access to your account</li>
                    <li>Damages exceeding the amount paid by you in the 12 months preceding the claim</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed">
                    Some jurisdictions do not allow the limitation of liability, so these limitations may not apply to you.
                  </p>
                </section>

                {/* Indemnification */}
                <section id="indemnification" className="mb-12 scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Indemnification</h2>
                  <p className="text-gray-700 leading-relaxed">
                    You agree to indemnify, defend, and hold harmless Inked.fit and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising out of or related to your use of the service, violation of these Terms, or infringement of any third-party rights.
                  </p>
                </section>

                {/* Changes to Terms */}
                <section id="changes" className="mb-12 scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
                  <p className="text-gray-700 leading-relaxed">
                    We may revise these Terms at any time by posting the updated Terms on our website. We will notify you of material changes via email or through the service. Your continued use of the service after such changes constitutes your acceptance of the new Terms. If you do not agree to the revised Terms, you must stop using the service.
                  </p>
                </section>

                {/* Contact */}
                <section id="contact" className="scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    If you have any questions about these Terms of Service, please contact us:
                  </p>
                  <ul className="text-gray-700 space-y-2">
                    <li><strong>Email:</strong> legal@inked.fit</li>
                    <li><strong>Website:</strong> www.inked.fit</li>
                  </ul>
                </section>

              </div>
            </main>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}